// stores/checklist-store.js
import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";
import { withShioriAccessContext } from "@/api/shiori-access-context.js";
import {
  createGuestChecklist,
  deleteGuestChecklist,
  fetchGuestChecklists,
  isGuestMode,
  updateGuestChecklist,
} from "@/utils/guest-session";

export const useChecklistStore = defineStore("checklist", {
  state: () => ({
    checklists: {}, // { shioriId: [checklist items] }
    deadlineChecklists: {}, // { shioriId: [deadline notify items] }
    loading: false,
    error: null,
  }),

  getters: {
    // しおりIDでチェックリストを取得
    getChecklistsBySioriId:
      (state) =>
      (shioriId, itemType = null) => {
        if (itemType) {
          return state.checklists[`${shioriId}:${itemType}`] || [];
        }
        return state.checklists[shioriId] || [];
      },

    // 未完了のチェックリストのみ取得
    getIncompleteChecklists: (state) => (shioriId) => {
      const items = state.checklists[shioriId] || [];
      return items.filter((item) => item.COMPLETED_FLG === "0");
    },

    // 完了済みのチェックリストのみ取得
    getCompletedChecklists: (state) => (shioriId) => {
      const items = state.checklists[shioriId] || [];
      return items.filter((item) => item.COMPLETED_FLG === "1");
    },

    // 期限が近いチェックリスト（3日以内）
    getUpcomingChecklists: (state) => (shioriId) => {
      const items = state.checklists[shioriId] || [];
      const today = new Date();
      const threeDaysLater = new Date(today);
      threeDaysLater.setDate(today.getDate() + 3);

      return items.filter((item) => {
        if (!item.DEADLINE || item.COMPLETED_FLG === "1") return false;
        const deadline = new Date(item.DEADLINE);
        return deadline >= today && deadline <= threeDaysLater;
      });
    },

    // 期限切れのチェックリスト
    getOverdueChecklists: (state) => (shioriId) => {
      const items = state.checklists[shioriId] || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return items.filter((item) => {
        if (!item.DEADLINE || item.COMPLETED_FLG === "1") return false;
        const deadline = new Date(item.DEADLINE);
        deadline.setHours(0, 0, 0, 0);
        return deadline < today;
      });
    },
  },

  actions: {
    /**
     * チェックリスト一覧を取得（絞り込み・ソート機能付き）
     */
    async fetchChecklists(shioriId, filters = {}) {
      this.loading = true;
      this.error = null;

      try {
        if (isGuestMode()) {
          let items = fetchGuestChecklists(shioriId);
          if (filters.item_type) {
            items = items.filter((item) => item.ITEM_TYPE === filters.item_type);
          }

          const cacheKey = filters.item_type
            ? `${shioriId}:${filters.item_type}`
            : shioriId;
          this.checklists[cacheKey] = items;
          if (!filters.item_type) {
            this.checklists[shioriId] = items;
          }
          return items;
        }

        const token = localStorage.getItem("token");
        const params = new URLSearchParams();

        // フィルター条件を追加
        if (
          filters.completedFlg !== undefined &&
          filters.completedFlg !== null
        ) {
          params.append("completed_flg", filters.completedFlg);
        }
        if (filters.deadlineFrom) {
          params.append("deadline_from", filters.deadlineFrom);
        }
        if (filters.deadlineTo) {
          params.append("deadline_to", filters.deadlineTo);
        }
        if (filters.categoryName) {
          params.append("category_name", filters.categoryName);
        }
        if (filters.assignedMemberCode) {
          params.append("assigned_member_code", filters.assignedMemberCode);
        }
        if (filters.sortBy) {
          params.append("sort_by", filters.sortBy);
        }
        if (filters.sortOrder) {
          params.append("sort_order", filters.sortOrder);
        }
        if (filters.item_type) {
          params.append("item_type", filters.item_type);
        }
        if (
          filters.deadline_notify_flg !== undefined &&
          filters.deadline_notify_flg !== null
        ) {
          params.append("deadline_notify_flg", filters.deadline_notify_flg);
        }

        const queryString = params.toString();
        const url = queryString
          ? `${API_URL}/checklist_detail/filtered/${shioriId}?${queryString}`
          : `${API_URL}/checklist_detail/filtered/${shioriId}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        response.data.forEach((item) => {
          item.ASSIGNED_MEMBER_PROFILE_IMAGE =
            item.ASSIGNED_MEMBER_HAS_PROFILE_IMAGE &&
            item.ASSIGNED_MEMBER_APP_USER_CODE
              ? `${API_URL}/users/${item.ASSIGNED_MEMBER_APP_USER_CODE}/profile-image`
              : null;
          (item.MEMBERS || []).forEach((member) => {
            member.PROFILE_IMAGE =
              member.HAS_PROFILE_IMAGE && member.APP_USER_CODE
                ? `${API_URL}/users/${member.APP_USER_CODE}/profile-image`
                : null;
          });
        });

        if (filters.deadline_notify_flg === "1") {
          this.deadlineChecklists[shioriId] = response.data;
        } else {
          const cacheKey = filters.item_type
            ? `${shioriId}:${filters.item_type}`
            : shioriId;
          this.checklists[cacheKey] = response.data;
          // 後方互換: 種別指定時もしおりIDキーへ同期（期限通知など）
          if (!filters.item_type) {
            this.checklists[shioriId] = response.data;
          }
        }
        return response.data;
      } catch (error) {
        console.error("チェックリスト取得エラー:", error);
        this.error =
          error.response?.data?.detail || "チェックリストの取得に失敗しました";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 拡張版チェックリスト作成
     */
    async createChecklist(checklistData) {
      this.loading = true;
      this.error = null;

      try {
        if (isGuestMode()) {
          const item = createGuestChecklist(
            checklistData.shioriId,
            checklistData.itemType || "TODO",
            {
              itemName: checklistData.itemName,
              memoText: checklistData.memoText,
              categoryName: checklistData.categoryName,
              deadline: checklistData.deadline,
              priorityLevel: checklistData.priorityLevel,
              deadlineNotify: checklistData.deadlineNotifyFlg === "1",
              members: (checklistData.assignedMemberCodes || []).map(
                (memberCode) => ({
                  MEMBER_CODE: memberCode,
                  ASSIGNEE_USER_CODE: memberCode,
                }),
              ),
            },
          );
          await this.fetchChecklists(checklistData.shioriId);
          await this.fetchChecklists(checklistData.shioriId, {
            item_type: checklistData.itemType || "TODO",
          });
          return item;
        }

        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${API_URL}/checklist_detail/enhanced/`,
          checklistData,
          withShioriAccessContext(checklistData.shioriId, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        );

        // ローカルステートを更新
        await this.fetchChecklists(checklistData.shioriId);

        return response.data;
      } catch (error) {
        console.error("チェックリスト作成エラー:", error);
        this.error =
          error.response?.data?.detail || "チェックリストの作成に失敗しました";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * チェックリスト更新
     */
    async updateChecklist(shioriId, pageNo, checklistNo, updateData) {
      this.loading = true;
      this.error = null;

      try {
        if (isGuestMode()) {
          const result = updateGuestChecklist(
            shioriId,
            pageNo,
            checklistNo,
            updateData,
          );
          await this.fetchChecklists(shioriId);
          await this.fetchChecklists(shioriId, {
            item_type: result.ITEM_TYPE || updateData.itemType || "TODO",
          });
          return result;
        }

        const token = localStorage.getItem("token");
        const response = await axios.put(
          `${API_URL}/checklist_detail/${shioriId}/${pageNo}/${checklistNo}`,
          updateData,
          withShioriAccessContext(shioriId, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        );

        return response.data;
      } catch (error) {
        console.error("チェックリスト更新エラー:", error);
        this.error =
          error.response?.data?.detail || "チェックリストの更新に失敗しました";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    patchChecklistLocal(
      shioriId,
      itemType,
      pageNo,
      checklistNo,
      updateData,
      shioriMembers = [],
    ) {
      const keys = [`${shioriId}:${itemType}`, shioriId];
      keys.forEach((key) => {
        const items = this.checklists[key];
        if (!Array.isArray(items)) return;

        const index = items.findIndex(
          (item) =>
            item.PAGE_NO === pageNo && item.CHECKLIST_NO === checklistNo,
        );
        if (index === -1) return;

        const selectedCodes = updateData.assignedMemberCodes || [];
        const members = selectedCodes.map((code) => {
          const member = shioriMembers.find((m) => m.MEMBER_CODE === code);
          return {
            ASSIGNEE_USER_CODE: code,
            MEMBER_NAME: member?.MEMBER_NAME || code,
            PROFILE_IMAGE: member?.PROFILE_IMAGE_BASE64 || null,
            COMPLETED_FLG: "0",
            COMPLETED_YMD: null,
          };
        });

        items[index] = {
          ...items[index],
          ITEM_NAME: updateData.itemName ?? items[index].ITEM_NAME,
          MEMO_TEXT: updateData.memoText ?? items[index].MEMO_TEXT,
          CATEGORY_NAME: updateData.categoryName ?? null,
          DEADLINE: updateData.deadline ?? null,
          DEADLINE_NOTIFY_FLG: updateData.deadlineNotifyFlg ?? "0",
          ASSIGNED_MEMBER_CODE:
            updateData.assignedMemberCode ??
            selectedCodes[0] ??
            items[index].ASSIGNED_MEMBER_CODE,
          PRIORITY_LEVEL:
            updateData.priorityLevel ?? items[index].PRIORITY_LEVEL,
          PRIVATE_FLG: updateData.privateFlg ?? items[index].PRIVATE_FLG,
          MEMBERS: members,
        };
      });
    },

    /**
     * チェックリスト削除
     */
    async deleteChecklist(shioriId, pageNo, checklistNo) {
      this.loading = true;
      this.error = null;

      try {
        if (isGuestMode()) {
          const target = fetchGuestChecklists(shioriId).find(
            (item) =>
              String(item.PAGE_NO) === String(pageNo) &&
              String(item.CHECKLIST_NO) === String(checklistNo),
          );
          const result = deleteGuestChecklist(shioriId, pageNo, checklistNo);
          await this.fetchChecklists(shioriId);
          if (target?.ITEM_TYPE) {
            await this.fetchChecklists(shioriId, {
              item_type: target.ITEM_TYPE,
            });
          }
          return result;
        }

        const token = localStorage.getItem("token");
        const response = await axios.delete(
          `${API_URL}/checklist_detail/${shioriId}/${pageNo}/${checklistNo}`,
          withShioriAccessContext(shioriId, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        );

        // ローカルステートを更新
        await this.fetchChecklists(shioriId);

        return response.data;
      } catch (error) {
        console.error("チェックリスト削除エラー:", error);
        this.error =
          error.response?.data?.detail || "チェックリストの削除に失敗しました";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async bulkDeleteChecklists(shioriId, items) {
      this.loading = true;
      this.error = null;

      try {
        if (isGuestMode()) {
          items.forEach((item) => {
            deleteGuestChecklist(shioriId, item.PAGE_NO, item.CHECKLIST_NO);
          });
          await this.fetchChecklists(shioriId);
          return { success: true };
        }

        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${API_URL}/checklist_detail/${shioriId}/bulk-delete`,
          {
            items: items.map((item) => ({
              page_no: item.PAGE_NO,
              checklist_no: item.CHECKLIST_NO,
            })),
          },
          withShioriAccessContext(shioriId, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        );

        return response.data;
      } catch (error) {
        console.error("チェックリスト一括削除エラー:", error);
        this.error =
          error.response?.data?.detail ||
          "チェックリストの一括削除に失敗しました";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * スケジュールからチェックリスト作成
     */
    async createFromSchedule(scheduleData) {
      this.loading = true;
      this.error = null;

      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${API_URL}/checklist_detail/from-schedule/`,
          scheduleData,
          withShioriAccessContext(scheduleData.shioriId, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        );

        // ローカルステートを更新
        await this.fetchChecklists(scheduleData.shioriId);

        return response.data;
      } catch (error) {
        console.error("スケジュールからチェックリスト作成エラー:", error);
        this.error =
          error.response?.data?.detail ||
          "スケジュールからチェックリストの作成に失敗しました";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * チェックリストの完了状態をトグル
     */
    async toggleComplete(shioriId, pageNo, checklistNo, currentStatus) {
      const newStatus = currentStatus === "1" ? "0" : "1";
      return await this.updateChecklist(shioriId, pageNo, checklistNo, {
        completedFlg: newStatus,
      });
    },

    /**
     * ステートをクリア
     */
    clearChecklists() {
      this.checklists = {};
      this.deadlineChecklists = {};
      this.error = null;
    },
    clearShioriChecklists(shioriId) {
      Object.keys(this.checklists).forEach((key) => {
        if (key === shioriId || key.startsWith(`${shioriId}:`)) {
          delete this.checklists[key];
        }
      });
      delete this.deadlineChecklists[shioriId];
      this.error = null;
    },

    /**
     * 共有リンク経由でのチェックリスト作成
     */
    async createSharedChecklist(shareToken, checklistData) {
      this.loading = true;
      this.error = null;
      try {
        const payload = {
          pageNo: String(checklistData.pageNo || checklistData.page_no || "1"),
          itemName: (checklistData.itemName || checklistData.item_name || "").trim(),
          categoryName: (checklistData.categoryName || checklistData.category_name || "").trim() || null,
          itemType: checklistData.itemType || checklistData.item_type || "TODO",
          deadline: checklistData.deadline || null,
          priorityLevel: checklistData.priorityLevel || checklistData.priority_level || "NORMAL",
          memoText: (checklistData.memoText || checklistData.memo_text || "").trim() || null,
          assignedMemberCodes: checklistData.assignedMemberCodes || checklistData.assigned_member_codes || checklistData.assigned_members || null,
        };

        const response = await axios.post(
          `${API_URL}/shiori/shared/${shareToken}/checklist`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("共有チェックリスト作成エラー:", error);
        this.error =
          error.response?.data?.detail || "チェックリストの作成に失敗しました";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 共有リンク経由でのチェックリスト更新（完了チェック・編集）
     */
    async updateSharedChecklist(shareToken, pageNo, checklistNo, updateData) {
      this.loading = true;
      this.error = null;
      try {
        const payload = {};

        if (updateData.itemName !== undefined || updateData.item_name !== undefined) {
          const val = updateData.itemName ?? updateData.item_name;
          payload.itemName = val ? String(val).trim() : null;
        }
        if (updateData.categoryName !== undefined || updateData.category_name !== undefined) {
          const val = updateData.categoryName ?? updateData.category_name;
          payload.categoryName = val ? String(val).trim() : null;
        }
        if (updateData.isChecked !== undefined) {
          payload.isChecked = Boolean(updateData.isChecked);
        }
        if (updateData.completedFlg !== undefined || updateData.completed_flg !== undefined) {
          payload.completedFlg = updateData.completedFlg ?? updateData.completed_flg;
        }
        if (updateData.deadline !== undefined) {
          payload.deadline = updateData.deadline || null;
        }
        if (updateData.priorityLevel !== undefined || updateData.priority_level !== undefined) {
          payload.priorityLevel = updateData.priorityLevel ?? updateData.priority_level;
        }
        if (updateData.memoText !== undefined || updateData.memo_text !== undefined) {
          const val = updateData.memoText ?? updateData.memo_text;
          payload.memoText = val ? String(val).trim() : null;
        }
        if (updateData.itemType !== undefined || updateData.item_type !== undefined) {
          payload.itemType = updateData.itemType ?? updateData.item_type;
        }
        if (
          updateData.assignedMemberCodes !== undefined ||
          updateData.assigned_member_codes !== undefined ||
          updateData.assigned_members !== undefined
        ) {
          payload.assignedMemberCodes =
            updateData.assignedMemberCodes ??
            updateData.assigned_member_codes ??
            updateData.assigned_members ??
            null;
        }

        const response = await axios.put(
          `${API_URL}/shiori/shared/${shareToken}/checklist/${pageNo}/${checklistNo}`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("共有チェックリスト更新エラー:", error);
        this.error =
          error.response?.data?.detail || "チェックリストの更新に失敗しました";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 共有リンク経由でのチェックリスト削除
     */
    async deleteSharedChecklist(shareToken, pageNo, checklistNo) {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.delete(
          `${API_URL}/shiori/shared/${shareToken}/checklist/${pageNo}/${checklistNo}`,
        );
        return response.data;
      } catch (error) {
        console.error("共有チェックリスト削除エラー:", error);
        this.error =
          error.response?.data?.detail || "チェックリストの削除に失敗しました";
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
});
